<template>
    <BaseDialog v-bind="$attrs" v-model:open="open" :title="t('components.slack.title')">
        <p class="body-small text-ink-muted">{{ t("components.slack.linkedWorkspaces") }}</p>

        <ul class="flex flex-col gap-3">
            <li v-for="workspace in workspaces" :key="workspace.team_id" :data-team-id="workspace.team_id" class="flex flex-col gap-2.5">
                <div class="flex flex-row justify-between items-center gap-3">
                    <span class="body-small text-ink truncate">
                        <i class="bi bi-check-circle-fill text-status-success mr-1" aria-hidden="true"></i>
                        {{ workspace.name || t("components.slack.workspaceFallback") }}
                    </span>
                    <div class="flex flex-row gap-2 shrink-0">
                        <a :href="workspace.app_url" target="_blank" rel="noopener noreferrer" class="base-btn-neutral base-btn-sm">{{ t("components.slack.openInSlack") }}</a>
                        <BaseButton v-if="confirmingTeamId !== workspace.team_id" theme="tertiary" size="sm" @click="confirmingTeamId = workspace.team_id">
                            {{ t("components.slack.disconnect") }}
                        </BaseButton>
                    </div>
                </div>
                <InlineConfirm
                    :open="confirmingTeamId === workspace.team_id"
                    :prompt="t('components.slack.disconnectPrompt', { workspace: workspace.name || t('components.slack.thisWorkspace') })"
                    :confirm-label="t('components.slack.confirmDisconnect')"
                    :is-confirming="isDisconnecting"
                    @update:open="(isOpen: boolean) => !isOpen && (confirmingTeamId = null)"
                    @confirm="disconnect(workspace.team_id)" />
            </li>
        </ul>
        <p v-if="disconnectError" role="alert" class="scaffold-details-small text-status-error">{{ t("components.slack.disconnectFailed") }}</p>
    </BaseDialog>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import InlineConfirm from "~vue/components/InlineConfirm.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineOptions({ inheritAttrs: false });

export type SlackWorkspaceLink = {
    team_id: string;
    name: string;
    app_url: string;
};

defineProps<{
    workspaces: SlackWorkspaceLink[];
}>();

const open = defineModel<boolean>("open");
const confirmingTeamId = ref<string | null>(null);
const disconnectingTeamId = ref<string | null>(null);

const { execute, error: disconnectError, isFetching: isDisconnecting } = useFetch(() => `/v1/slack/bindings/${disconnectingTeamId.value}`, { immediate: false }).delete();

const disconnect = async (teamId: string) => {
    disconnectingTeamId.value = teamId;
    await execute();
    if (disconnectError.value) {
        return;
    }
    confirmingTeamId.value = null;
    logUserInteraction("slack_integration_disconnected");
    router.reload({ only: ["slack_connected", "slack_app_url", "slack_workspaces"] });
};

// The component stays mounted across open/close, so a half-finished confirmation shouldn't survive a reopen.
watch(open, (isOpen) => {
    if (!isOpen) {
        confirmingTeamId.value = null;
    }
});
</script>
